/**
 * Convert minutes to hours and minutes format
 * @param minutes - Total minutes to convert
 * @param hoursLabel - Optional label for hours (default "h")
 * @param minutesLabel - Optional label for minutes (default "m")
 * @returns Formatted string like "1h 30m" or "45m"
 */
export function formatMinutesToHM(
    minutes: number,
    hoursLabel: string = 'h',
    minutesLabel: string = 'm'
): string {
    if (minutes === 0) return `0${minutesLabel}`
    const hours = Math.floor(minutes / 60)
    const mins = Math.floor(minutes % 60)
    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}${hoursLabel}`)
    if (mins > 0) parts.push(`${mins}${minutesLabel}`)
    return parts.join(' ') || `0${minutesLabel}`
}

export interface FormatSecondsToDHMOptions {
    /** When true, always include minutes (e.g. "1d 3h 0m"). Use for elapsed/time remaining. */
    alwaysShowMinutes?: boolean
}

/**
 * Convert seconds to days, hours, and minutes format
 * @param seconds - Total seconds to convert
 * @param options - Optional: alwaysShowMinutes to always include minutes (no seconds)
 * @returns Formatted string like "2d 3h 45m" or "3h 45m" or "45m"
 */
export function formatSecondsToDHM(seconds: number, options?: FormatSecondsToDHMOptions): string {
    const absSeconds = Math.floor(Math.abs(seconds))
    const sign = seconds < 0 ? '-' : ''
    if (absSeconds === 0) return '0m'

    const days = Math.floor(absSeconds / (24 * 60 * 60))
    const hours = Math.floor((absSeconds % (24 * 60 * 60)) / (60 * 60))
    const mins = Math.floor((absSeconds % (60 * 60)) / 60)

    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    if (options?.alwaysShowMinutes) {
        parts.push(`${mins}m`)
    } else if (mins > 0) {
        parts.push(`${mins}m`)
    }

    return sign + (parts.join(' ') || '0m')
}

/**
 * Convert seconds to days, hours, minutes, and seconds format (for elapsed / time remaining)
 * @param totalSeconds - Total seconds (can be negative for overdue)
 * @returns Formatted string like "98d 6h 2m 0s" or "-98d 4h 53m 0s"
 */
export function formatSecondsToDHMS(totalSeconds: number): string {
    const sign = totalSeconds < 0 ? '-' : ''
    const abs = Math.abs(Math.floor(totalSeconds))
    const days = Math.floor(abs / (24 * 60 * 60))
    const hours = Math.floor((abs % (24 * 60 * 60)) / (60 * 60))
    const mins = Math.floor((abs % (60 * 60)) / 60)
    const secs = abs % 60
    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    if (mins > 0) parts.push(`${mins}m`)
    parts.push(`${secs}s`)
    return sign + parts.join(' ')
}

